import React, { useRef } from 'react'
import {
  Box,
  Flex,
  Stack,
  Text,
  AlertDialogCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react'
import { Button, ModalFooterAction } from '@apex/component-library'
import TransferDetailsPreview from './ModalTransferDetailsPreview'
import { useFormikContext } from 'formik'
import { ACHTransferValues } from './types'
import formatPayload from './formatPayload'
import { SearchAccountsResponsePayload, IraConstraintsResponsePayload } from '@Types'
import useAchSubmission from '../../api/useAchSubmission'
import ModalDistributionDetailsPreview from '../TransferPreviewDetails/ModalDistributionDetailsPreview'
import ModalContributionDetailsPreview from './ModalContributionDetailsPreview'
import ModalTransferScheduledPreview from '../ModalTransferScheduledPreview/ModalTransferScheduledPreview'
import ModalTransferFeesPreview from '../ModalComponents/ModalTransferFeesPreview'
import { TransferCutOff } from '../../components/TransferCutOff'
import { TransferDirection } from '../../api/useAchSubmission'
import useGetSchedules from '../../api/useGetSchedules'
import { capitalize } from 'lodash'
import { delayFn } from '../../utils'
import { useCreateScheduleTransfer } from '../../api/useCreateScheduledTransfer'
import useGetRecentCashActivity from '../../api/useGetRecentCashActivity'
import TransferAmountWarning from '../TransferAmountWarning'

export interface ACHPreviewModalProps {
  account: SearchAccountsResponsePayload
  accountName?: string
  fromAccount?: string
  hasSubmitPermission: boolean
  transferDirection: TransferDirection
  onClose: () => void
  isOpen: boolean
  iraConstraints?: IraConstraintsResponsePayload | null
  amountAvailable: number
}

const ACHPreviewModal = ({
  account,
  hasSubmitPermission,
  transferDirection,
  onClose,
  isOpen,
  iraConstraints,
  amountAvailable
}: ACHPreviewModalProps) => {
  const formik = useFormikContext<ACHTransferValues>()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { refetch: refetchRecentActivityData } = useGetRecentCashActivity(account.accountNumber)
  const { refetch: refetchSchedulesData } = useGetSchedules(account.accountNumber)

  const submitAchTransfer = useAchSubmission(transferDirection, {
    onSuccess: () => {
      onClose()
      delayFn(refetchRecentActivityData)
      formik.resetForm()
      formik.validateForm()
    },
    amountAvailable,
    isFullDisbursement: formik.values.totalBalanceCheckbox,
    amount: formik.values.transferAmountRaw
  })

  const relationshipId = (): string | undefined => {
    const isWithdrawal = formik.values.toAccount === account.accountNumber
    if (isWithdrawal) {
      return formik.values.fromAccount
    }
    return formik.values.toAccount
  }

  const handleSubmitSuccess = () => {
    formik.resetForm()
    formik.validateForm()
  }

  const handleOnSettle = () => {
    refetchSchedulesData()
    onClose()
  }

  const submitAchScheduledTransfer = useCreateScheduleTransfer('achs', transferDirection, {
    onSuccess: handleSubmitSuccess,
    onSettled: handleOnSettle
  })

  const onSubmit = () => {
    const isScheduledTransfer =
      formik.values.transferRecurrence === 'RECURRING' || formik.values.whenToSend === 'IN_THE_FUTURE'
    if (isScheduledTransfer) {
      submitAchScheduledTransfer.mutate(
        formatPayload(formik.values, transferDirection, isScheduledTransfer, iraConstraints, relationshipId())
      )
    } else {
      submitAchTransfer.mutate(
        formatPayload(formik.values, transferDirection, isScheduledTransfer, iraConstraints, relationshipId())
      )
    }
  }

  const getDirectionSingular = (transferDirection: TransferDirection) => {
    if (transferDirection === 'deposits') return 'deposit'
    if (transferDirection === 'withdrawals') return 'withdrawal'
  }

  return (
    <Box>
      <AlertDialog
        motionPreset='slideInBottom'
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent w='100%' maxW='600px' data-testid='achModal'>
          <AlertDialogHeader textStyle='h1' data-testid='ach-modal-header'>
            ACH {capitalize(getDirectionSingular(transferDirection))}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Stack pb={8} spacing={8}>
              <Text>Please confirm the details of your transfer below.</Text>
              <Stack spacing={6}>
                <TransferDetailsPreview
                  transferAmount={formik.values.transferAmountRaw}
                  fromAccount={formik.values.fromAccountName}
                  isTotalAmount={formik.values.totalBalanceCheckbox}
                  toAccount={formik.values.toAccountName}
                />
                <ModalTransferScheduledPreview
                  transferRecurrence={formik.values.transferRecurrence}
                  whenToSend={formik.values.whenToSend}
                  transferScheduledDate={formik.values.transferScheduledDate}
                  transferStartDate={formik.values.transferStartDate}
                  transferFrequency={formik.values.transferFrequency}
                  numberOfTransfers={formik.values.numberOfTransfers}
                />
                {formik.values.distributionReason && (
                  <ModalDistributionDetailsPreview
                    distributionReason={formik.values.distributionReason}
                    federalTaxWithholdingType={formik.values.federalTaxWithholdingType}
                    federalTaxWithholdingAmountRaw={formik.values.federalTaxWithholdingAmountRaw}
                    stateTaxWithholdingType={formik.values.stateTaxWithholdingType}
                    stateTaxWithholdingAmountRaw={formik.values.stateTaxWithholdingAmountRaw}
                  />
                )}
                {formik.values.contributionType && (
                  <ModalContributionDetailsPreview
                    contributionType={formik.values.contributionType}
                    contributionYear={formik.values.contributionYear}
                  />
                )}
                <ModalTransferFeesPreview fees={formik.values.fees} />
              </Stack>
              <TransferCutOff pollLocalTime={true} />
              {transferDirection === 'withdrawals' && (
                <TransferAmountWarning
                  amountAvailable={amountAvailable}
                  transferAmount={formik.values.transferAmountRaw}
                />
              )}
            </Stack>
          </AlertDialogBody>
          <ModalFooterAction>
            <Flex>
              <Box ml='auto'>
                <Button variant='white' ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  ml={3}
                  onClick={onSubmit}
                  isLoading={submitAchTransfer.isLoading}
                  isDisabled={submitAchTransfer.isLoading || !hasSubmitPermission}
                >
                  Confirm
                </Button>
              </Box>
            </Flex>
          </ModalFooterAction>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  )
}

export default ACHPreviewModal
